import { SettingsBackupRestoreOutlined } from "@mui/icons-material";
import { type FC, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SystemDialog from "../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";

type Props = {
	/** Replaces the user's printouts with the department's; resolves whether it worked (a failure is already shown). */
	onConfirm: () => Promise<boolean>;
	/** The replacement is on its way. */
	running: boolean;
};

/**
 * "Use department printouts": puts the department's Printout 1 settings and saved printouts in place of
 * the user's own, after a confirm that names what is lost — their own saved printouts, for good.
 */
export const UseDepartmentPrintoutsButton: FC<Props> = ({ onConfirm, running }) => {
	const { t } = useTranslation();
	const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
	/** A second confirm click in the same frame (before `running` re-renders) must not start a second run. */
	const confirmingRef = useRef<boolean>(false);

	const confirm = async (): Promise<void> => {
		if (confirmingRef.current) return;
		confirmingRef.current = true;
		try {
			await onConfirm();
		} finally {
			confirmingRef.current = false;
			// Closed either way: a failure is already on screen as a snackbar, and the button is there to retry.
			setIsDialogOpen(false);
		}
	};

	return (
		<>
			<SystemButton
				data-testid="printout-use-department-button"
				buttonVariant="secondary"
				size="small"
				onClick={() => setIsDialogOpen(true)}
				loading={running}
				leadingIcon={<SettingsBackupRestoreOutlined />}
			>
				{t("printout.restore.department")}
			</SystemButton>
			<SystemDialog
				open={isDialogOpen}
				// Stays open until the replacement is done: the dialog is modal, so nothing in the popup can be
				// edited mid-way — an edit made then would save over the department's printouts afterwards.
				onClose={() => {
					if (!running && !confirmingRef.current) setIsDialogOpen(false);
				}}
				title={t("printout.restore.department.confirm.title")}
				body={t("printout.restore.department.confirm.body")}
				confirm={t("printout.restore.department")}
				cancel={t("printout.restore.department.confirm.cancel")}
				onConfirm={() => void confirm()}
				confirmLoading={running}
				confirmDisabled={running}
				dataTestId="printout-use-department-dialog"
				confirmDataTestId="printout-use-department-confirm"
				cancelDataTestId="printout-use-department-cancel"
			/>
		</>
	);
};
